import React, { useState } from 'react';
import {
  Badge,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { FunnelInsightFilters } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { formatOverlayWindowLabel } from './AnalyticsHonestyBanner';
import { FunnelUnitToggle, type FunnelUnit } from '../funnelUnit';

/**
 * Canvas report-mode header cluster (P-6): rendered in TopControlPanel's
 * customActions slot INSTEAD of the edit actions while the analytics overlay
 * owns the canvas. Presentational — the editor owns every piece of state.
 *
 * The version chip is a read-only mirror (P-8): the version SWITCHER stays on
 * the honesty banner inside the canvas. «Filters · N» counts every applied
 * clause across variables, traits and context dims (P-11).
 */

/** One clause = one scalar context filter or one trait key (values within a key are OR'd). */
export function countAppliedFilterClauses(filters: FunnelInsightFilters | undefined): number {
  if (!filters) return 0;
  let count = 0;
  if (filters.country) count += 1;
  if (filters.device) count += 1;
  if (filters.variant) count += 1;
  if (filters.attributionStatus) count += 1;
  const traits = filters.userTraits ?? {};
  count += Object.keys(traits).filter(key => (traits[key] ?? []).length > 0).length;
  const excludes = filters.userTraitsExclude ?? {};
  count += Object.keys(excludes).filter(key => (excludes[key] ?? []).length > 0).length;
  if (!filters.userTraits && filters.userTraitKey && filters.userTraitValue) count += 1;
  return count;
}

const shortVersionId = (versionId: string): string =>
  versionId.length > 8 ? `${versionId.slice(0, 8)}…` : versionId;

export interface ReportModeBarProps {
  /** Leaves report mode — the extracted toggle-OFF path (P-10). */
  onExit: () => void;
  versionId?: string;
  versionName?: string;
  /** Starts of the current version when the trafficked rollup knows it. */
  versionStarts?: number;
  /** Report window carried from the Open deep link (absent = all-time). */
  windowFrom?: string;
  windowTo?: string;
  /** Applied filters — drives the «Filters · N» badge (P-11). */
  filters?: FunnelInsightFilters;
  /** Filter surface rendered inside the Filters popover. */
  filtersContent?: React.ReactNode;
  refreshing?: boolean;
  onRefresh: () => void;
  /** D-F1 honesty marker — the payload on screen is a saved snapshot. */
  servedFromCache?: boolean;
  /** Epoch ms or ISO string — only fed to `new Date(...)`, both work. */
  cachedAt?: number | string | null;
  /**
   * F2 (FT-AC-083): page-level counting unit. Both absent = the toggle is not
   * rendered (legacy hosts stay byte-identical).
   */
  unit?: FunnelUnit;
  onUnitChange?: (unit: FunnelUnit) => void;
}

export const ReportModeBar: React.FC<ReportModeBarProps> = ({
  onExit,
  versionId,
  versionName,
  versionStarts,
  windowFrom,
  windowTo,
  filters,
  filtersContent,
  refreshing = false,
  onRefresh,
  servedFromCache = false,
  cachedAt,
  unit,
  onUnitChange,
}) => {
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const filtersCount = countAppliedFilterClauses(filters);
  const versionLabel = versionName || (versionId ? shortVersionId(versionId) : '');

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      // F2 review fix (900px): the unit toggle widened the bar — wrap instead
      // of clipping the trailing controls (Filters/refresh) off-viewport.
      flexWrap="wrap"
      useFlexGap
      rowGap={0.5}
      data-testid={ANALYTICS_CANVAS_TEST_IDS.reportModeBar}
    >
      <Button
        size="small"
        color="inherit"
        startIcon={<CloseIcon fontSize="small" />}
        onClick={onExit}
        data-testid={ANALYTICS_CANVAS_TEST_IDS.reportModeExit}
        // compact idiom of the onboarding-canvas header actions
        // (OnboardingEditorActionsV2) — plain size="small" renders larger here
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          fontSize: '0.8125rem',
        }}
      >
        Exit report
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        Report · read-only
      </Typography>
      {/* F2 (D3): the unit switch lives between the mode caption and the
          version chip — a page-level scope control on the page-level bar. */}
      {unit && onUnitChange && (
        <FunnelUnitToggle
          unit={unit}
          onChange={onUnitChange}
          testIds={{
            root: ANALYTICS_CANVAS_TEST_IDS.unitToggle,
            option: ANALYTICS_CANVAS_TEST_IDS.unitToggleOption,
          }}
        />
      )}
      {versionLabel && (
        <Chip
          data-testid={ANALYTICS_CANVAS_TEST_IDS.reportModeVersionChip}
          label={`Version: ${versionLabel}${typeof versionStarts === 'number'
            ? ` · ${versionStarts.toLocaleString('en-US')} starts`
            : ''}`}
          size="small"
          variant="outlined"
        />
      )}
      <Chip
        data-testid={ANALYTICS_CANVAS_TEST_IDS.reportModeWindowChip}
        label={formatOverlayWindowLabel(windowFrom, windowTo)}
        size="small"
        variant="outlined"
      />
      <Badge badgeContent={filtersCount} color="primary" overlap="rectangular">
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterListIcon fontSize="small" />}
          onClick={event => setFiltersAnchor(event.currentTarget)}
          data-testid={ANALYTICS_CANVAS_TEST_IDS.reportModeFilters}
          sx={{
            textTransform: 'none',
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            fontSize: '0.8125rem',
          }}
        >
          Filters
        </Button>
      </Badge>
      <Popover
        open={Boolean(filtersAnchor)}
        anchorEl={filtersAnchor}
        onClose={() => setFiltersAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 640 } } }}
      >
        {filtersContent}
      </Popover>
      <Tooltip title="Refresh funnel insights">
        <span>
          <IconButton
            aria-label="refresh funnel insights"
            data-testid={ANALYTICS_CANVAS_TEST_IDS.refresh}
            size="small"
            disabled={refreshing}
            onClick={onRefresh}
          >
            {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      {servedFromCache && (
        <Tooltip title={cachedAt
          ? `Saved snapshot from ${new Date(cachedAt).toLocaleString()} — refresh to reload`
          : 'Saved snapshot — refresh to reload'}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={ANALYTICS_CANVAS_TEST_IDS.overlayCachedMarker}
          >
            cached
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
};
